import React from "react";
import {
    captureOAuthCallback,
    exchangeGoogleCode,
    finishGoogleFlow,
    hasPendingGoogleExchange
} from "services/googleOAuth";
import {Redirect} from "react-router-dom";
import authService from "services/authService";
import {beginGoogleSignup, clearGoogleSignup, restoreGoogleSignup} from "services/googleSignupSession";
import {loadingComponent} from "config/config";
import ValidationForm from "validation/form";
import Row from "reactstrap/lib/Row";
import Col from "reactstrap/lib/Col";
import Joi from "joi-browser";
import Alert from "reactstrap/lib/Alert";

class OAuth2RedirectHandler extends ValidationForm {
  state = {
    data: {},
    errors: {},
    displayHomePage: false,
    doValidation: false,
      sessionReady: false,
      exchangeError: null,
    referralCode: 0,
  };

  schema = {
    phoneNumber: Joi.string()
      .required()
      .label("Phone Number")
      .regex(/^\+(?:[0-9]●?){6,14}[0-9]$|^[0]\d{10}$/g)
      .error((errors) => {
        errors.forEach((err) => {
          switch (err.type) {
            case "any.empty":
              err.message = '"Phone Number" should not be empty';
              break;
            default:
              err.message = "Enter a valid Phone Number";
              break;
          }
        });
        return errors;
      }),
  };

  componentDidMount() {
      // First operation: scrub the URL, including if storage/referral processing fails later.
      try {
          this.callback = captureOAuthCallback(this.props.location, this.props.history);
      } catch (_) {
          this.setState({exchangeError: "Google sign-in expired or failed. Please try again."});
          return;
      }
    let referralCode = 0;
      const ref = this.callback.ref;
      if (ref && !isNaN(ref)) {
          referralCode = ref;
          try {
              authService.updateReferralCode(ref);
          } catch (_) { /* Referral remains in component state. */
          }
    } else {
          try {
              referralCode = authService.getReferralCode() || 0;
              authService.updateReferralCodeHeader();
          } catch (_) { /* Storage denial must not undo URL cleanup or prevent exchange. */
          }
    }
    this.setState({ referralCode });
      this.exchangeCode();
  }

    componentWillUnmount() {
        this.unmounted = true;
    }

    async exchangeCode() {
        if (this.exchangeStarted) return;
        this.exchangeStarted = true;
        try {
            const callback = this.callback || captureOAuthCallback(this.props.location, this.props.history);
            const signup = this.props.location.pathname.endsWith("/signup");
            if (signup && !callback.failed && callback.codes && callback.codes.length === 0 && !hasPendingGoogleExchange(callback.pathname)) {
                const referralCode = await restoreGoogleSignup();
                if (!this.unmounted) this.setState({sessionReady: true, referralCode});
                return;
            }
            const token = await exchangeGoogleCode(callback);
            if (this.unmounted) return;
            if (signup) {
                await beginGoogleSignup(token, this.state.referralCode);
                await restoreGoogleSignup();
                if (!this.unmounted) this.setState({sessionReady: true});
            } else {
                authService.updateToken("Bearer " + token);
                await authService.ping(this);
            }
        } catch (_) {
            if (!this.unmounted) this.setState({exchangeError: "Google sign-in expired or failed. Please try again."});
        }
  }

    async completeSignUp() {
        if (this.completing || !this.state.sessionReady) return;
        this.completing = true;
        this.setState({doValidation: true});
        try {
            await restoreGoogleSignup();
            if (this.unmounted) return;
            await authService.completeSignUp(this, this.state.data.phoneNumber, this.state.referralCode);
        } catch (_) {
            this.completing = false;
            this.setState({doValidation: false});
        }
  }

  tokenIsValid() {
      if (this.props.location.pathname.endsWith("/signup")) clearGoogleSignup();
      finishGoogleFlow(this.props.location.pathname);
      if (this.unmounted) return;
    this.setState({ displayHomePage: true });
  }

  render() {
      if (this.state.exchangeError) return this.redirectToLoginPage(this.state.exchangeError);
      if (this.state.displayHomePage) return this.redirectToHomePage();
      if (!this.state.sessionReady || this.state.doValidation) return <>{loadingComponent()}</>;
      if (this.props.location.pathname.endsWith("/signup")) return this.capturePhoneNumber();
      return <>{loadingComponent()}</>;
  }

  redirectToHomePage() {
    const currentUser = authService.getCurrentUser();
    return (
      <Redirect
        to={{
          pathname: authService.resolveLandingPageUrl(),
          state: {
              // from: { pathname: this.props.location.pathname },
            fullname: currentUser.firstname + " " + currentUser.lastname,
          },
        }}
      />
    );
  }

  redirectToLoginPage(errorMessage) {
    return (
      <Redirect
        to={{
          pathname: "/signin",
          state: {
              from: {pathname: this.props.location.pathname},
            error: errorMessage,
          },
        }}
      />
    );
  }

  capturePhoneNumber() {
    return (
      <>
        <form
          onSubmit={(e) => {
            this.handleSubmit({
              e,
                submitFunction: () => this.completeSignUp(),
            });
          }}
        >
          <Row className="mt-5">
            <Col className="offset-md-3 col-md-6">
              <Alert color="dark">
                <Row>
                  <Col className="col-8">
                    <h2 className="font-weight-bold text-white text-underline">
                      One More Step to Go!{" "}
                    </h2>
                  </Col>
                  <Col>
                    <div className="bg-secondary rounded p-1">
                      <img
                        className="img-fluid"
                        alt="..."
                        src={require("assets/img/brand/wirepackage-colour.png")}
                      />
                    </div>
                  </Col>
                </Row>
                <p className="font-weight-bold mt-2">
                  Please enter your phone number to complete sign up.
                </p>
              </Alert>
            </Col>
          </Row>
          <Row>
            <Col className="offset-md-4 col-md-4">
              {this.renderStyledInput({
                mandatory: true,
                type: "text",
                name: "phoneNumber",
                placeholder: "Phone Number",
                icon: "fas fa-phone",
                label: "Phone Number",
              })}
            </Col>
          </Row>
          <Row>
            <Col className="offset-md-4 col-md-4">
              {this.renderButton(
                <>
                  <div>Complete Sign Up</div>
                </>,
                "btn-primary btn-block",
                "Complete Sign Up"
              )}
            </Col>
          </Row>
        </form>
      </>
    );
  }
}

export default OAuth2RedirectHandler;
